import './style.css';
import { S } from './state.js';
import { BAL, _cdFloor, fetchKnobs } from './config.js';
export { knobsUnknownKeys } from './config.js';   // re-exported so the smoke/lint gate can validate knobs.json ⊆ BAL
export { ITEMS } from './data/items.js';          // re-exported so the gate can check the reference docs cover every item
import { SAVE_KEY, GAME_VERSION, DEV, UPGRADES, UNLOCKS, ACHIEVEMENTS } from './data/progression.js';
import { TIERS, ITEMS, ARCH_INFO, GEAR_TAGS, SYNERGY_INFO } from './data/items.js';
import { GEAR_TIERS, GEAR_AFFIXES, GEAR_NOUNS, GEAR_ICONS, LEGENDARY_UNIQUES, LUNAR_UNIQUES } from './data/gear.js';
import { TYPES, rnd, rrange } from './fx.js';
import { Audio } from './audio.js';
import { WEAPONS, WEAPON_IDX, MODE_RULES, PROC_KEYS, ITEM_TAGS, ITEM_CAPPED,
  validateData, buildPrereqNote, prereqMet, offerWeightMul, evoEffectStr,
  weaponInteractionNote, gearInteractionNote } from './data/weapons.js';
import { validateBossKits } from './data/bosses.js';   // boss ability-kit lint (rides the DATA LINT gate)
import { validateRelics, validateRelicSets, RELICS } from './data/relics.js';   // relic data lint (rides the DATA LINT gate) + registry for the panel/sim
import { characterPanel } from './progression.js';   // re-exported on SIM for headless UI smoke-testing
import { buyUpgrade, perkDiffLines, recomputeStats, statDiffLines, upgradeCost } from './stats.js';
import { abbr, bg, drawCoins, drawEffects, drawMinimap, drawParticles, drawPickupArrows, drawPickups, drawPlayer, drawShrines, drawStatsPanel, fmtTime, glowSprite, gunCadence, gunFloor, hexA, hudBottomY, paperDps } from './render.js';
import { hud } from './touch-hud.js';
import { checkUpdate, drawTooltips, fetchGlobal, fitText, fitTip, submitRun, submitScore, wrapText } from './ui.js';
import { render, roundRect } from './screens.js';
import { die, update } from './physics.js';
import { autoRing, buyGear, closeShop, gearTier, grantGear, makeGear, openShop } from './shop.js';
import { tryDash } from './allies.js';
import { applyBurn, applyChill, applyOnHitStatuses, applyPoison, combatHeal, damageEnemy, detonateDot, fireWeapon, hurtPlayer, overload, startWave } from './combat.js';
import { LEVEL_STATS, buyShrineDraft, closeShrineDraft, confirmBlood, confirmPandora, rerollBossReward, takeBossReward, takeLevelUp, tryInteract, xpCost } from './shrines.js';
import { emitShock, popText, spawnParticle } from './procs.js';
import { aim, joy, startRun } from './input.js';
import { ctx } from './canvas.js';
import { clampToWorld, generateMap, pushOutOfRocks } from './world.js';
import { player } from './entities.js';
import { cam } from './meter.js';
import { checkAchievements, checkUnlocks } from './achievements.js';
import { COMPANION_IDX, grantItem, maybeDropRelic, persist, randomItemOfTier, loadSave } from './economy.js';
/* VOIDGUNNER — bullet-hell roguelite, modularized into src/*.js (see imports above).
   main.js now holds run-state init, world/entity/companion/achievement glue, and the boot loop. */
// ---- Biomes (palette shifts every 5 waves so long runs change scenery) ----
const BIOMES = [
  { sky: ['#11183a', '#1b2350', '#2a2466'], star: 'rgba(255,255,255,.5)' },
  { sky: ['#0b2a2a', '#103b3b', '#0e4a3a'], star: 'rgba(200,255,230,.5)' },
  { sky: ['#2a1030', '#3a1545', '#481a3a'], star: 'rgba(255,210,245,.5)' },
  { sky: ['#301a10', '#45260f', '#4a1a1a'], star: 'rgba(255,230,200,.5)' },
  { sky: ['#101a30', '#142a4a', '#0e2a5a'], star: 'rgba(200,225,255,.5)' },
];
export function currentBiome() { return BIOMES[Math.floor(Math.max(0, S.waveNum - 1) / 5) % BIOMES.length]; }

// ---- Cross-cutting run state added by the new systems ----
S.hazards = [], S.hazardTimer = 240;   // damaging void pools (telegraphed)
S.mortars = [];                      // mortar shells: telegraphed one-shot AoE blasts
export const BOSS_TYPES = ['warden', 'spiral', 'summoner', 'artillery', 'nova'];
export const BOSS_NAMES = { warden: 'The Warden', spiral: 'Spiral Core', summoner: 'Hive Queen', artillery: 'Bombardier', nova: 'Nova Tyrant' };
S.pendingBossType = 'warden';        // chosen in startWave, read by spawnEnemy/materializeEnemy
S.arena = { active: false, x: 0, y: 0, r: 600 };   // boss arena: a circular wall you fight inside
S.pendingBossAffix = null;           // deep-wave elite modifier for the next boss (swift/volley/echo), or null
S.pendingBossDuo = false;            // true when this boss wave spawns a SECOND boss (deep waves) — gates the duo HP cut
S.bossIntro = null;                  // { t, name } — arena-seal intro beat: VERSUS card + camera zoom-punch
S.dmgTexts = [];                     // floating damage numbers
S.flash = 0;   // cascade screen flash
S.pendingUnlocks = [];               // unlocks earned this death, shown on the menu
S.relics = {};                       // equipped-relic flag map (rebuilt by recomputeStats from S.save.relicLoadout)
S.relicLv = {};                      // equipped-relic star level by flag (0.26 fusion: 1..3; scales the upside)
S.lastStandMul = 1;                  // Final Hour relic: cached ≥85%-HP damage mult (read in laserHit)
S.relicToasts = [];                  // relic-drop notifications (mirrors S.achToasts)
S.phoenixUsed = false;               // Phoenix Pact relic: spent its once-per-run cheat-death (reset each run)
S.pendingAccountLevels = 0;          // account levels gained this run (surfaced on the death menu / panel button glow)
S.lastRunXp = 0;                     // meta-XP earned last run (shown on the Run Over screen)
S.showCharacter = false;             // Character Panel overlay (C / touch button / menu chip)
S.charTab = 'loadout';               // loadout | relics | stats
S.charScroll = 0; S.charScrollMax = 0; S.charPickSlot = null;   // panel scroll + the slot awaiting a relic pick
S._sfxCoin = 0, S._sfxHit = 0, S._sfxShoot = 0;   // per-frame high-frequency sfx budgets

export function popDmg(x, y, amt, crit) {
  S.dmgTexts.push({ x: x + rrange(-6, 6), y, life: 1, val: Math.round(amt), crit: !!crit });
  if (S.dmgTexts.length > 80) S.dmgTexts.shift();
}

// ---- Combat state ----
S.enemies = [], S.bullets = [], S.lasers = [], S.pbullets = [], S.warnings = [];   // warnings telegraph spawns
S.waveNum = 0, S.waveTimer = 180, S.waveBanner = null;
S.fireCooldown = 0, S.killsRun = 0, S.trickleT = 60;
S.fervor = 0, S.fervorTier = 0;                          // momentum meter + its tier
S.waveKills = 0, S.waveQuota = 0, S.waveCleared = false, S.waveAge = 0;   // wave-clear tracking
S.waveEvent = null, S.eventSpeedMul = 1, S.eventCoinMul = 1;   // special-wave modifiers (kick in ≥ wave 12, never on boss waves)
S.freeRerolls = 0, S.nextShopBoon = false;              // shop perks from clearing / high fervor
S.threat = 1;                                         // time/wave difficulty multiplier (hpPenalty declared earlier)
// (WAVE_INTERVAL / BULLET_SPEED live in the applyBalAliases() block up top — knobs can retune them)

// ---- Movement / arena state ----
export const keys = { up: false, down: false, left: false, right: false };
S.survival = 0;       // frames survived this run
S.shrineTimer = 90;   // countdown to the next shrine spawn

// ---- Wave shop state ----
S.shopOpen = false, S.shopOffer = [], S.rerollCost = 0, S.rerollsThisShop = 0, S.shopPicked = false;

export function reset() {
  S.rngSeed = (Math.floor(performance.now()) % 100000) + 7;
  S.particles = []; S.coins = []; S.pickups = []; S.shrines = []; S.effects = [];
  S.chainCount = 0; S.chainTimer = 0;
  S.runBonusDmg = 0; S.veteran = 0; S.hpPenalty = 0;   // run-growth + Blood-Shrine HP debt reset BEFORE the first recomputeStats below (else last run's penalty leaks into this run's spawn HP)
  S.weapon = WEAPON_IDX[S.save.startWeapon] || 0;   // start with the gun chosen on the menu
  S.items = {};
  if ((S.save.unlocks || {}).startitem) {   // Lucky Start unlock: open with a free common item
    const si = randomItemOfTier('common'); S.items[si.key] = (S.items[si.key] || 0) + 1;
  }
  recomputeStats();   // items are per-run — start each run fresh
  player.x = 0; player.y = 0; player.vx = 0; player.vy = 0;
  player.alive = true; player.trail = [];
  S.equipment = []; recomputeStats();   // gear is per-run, unbounded
  player.hp = S.MAX_HP; player.iFrames = BAL.player.iFramesSpawn; player.shield = S.MAX_SHIELD; player.shieldCds = [];  // spawn-in grace
  player.poison = 0; player.poisonT = 0; player.slowT = 0; player.slowAmt = 0;   // clear affix statuses
  player.recentDmg = 0;   // fresh double-tap mercy budget
  player.braceT = 0; S.wardLeft = 0; S.swWave = -1; S.titheN = 0;   // 0.17 item state (Bracing / Ward Sigil / Second Wind / Tithe)
  S.dynDmg = 1; S.dynHp = 1; S.dynDpsEma = 0.02; S.dynTtkEma = 240; S.dynDmgAcc = 0;   // dynamic pressure starts neutral
  player.dashStock = S.DASH_CHARGES; player.dashRe = S.DASH_RECHARGE; player.dashing = 0;
  S.enemies = []; S.bullets = []; S.lasers = []; S.pbullets = []; S.warnings = [];
  S.waveNum = 0; S.waveTimer = 90; S.waveBanner = null; S.fireCooldown = 0; S.killsRun = 0; S.trickleT = 60;   // first wave arrives quickly
  S.shopOpen = false; S.shopOffer = []; S.rerollCost = 0; S.rerollsThisShop = 0; S.shopPicked = false;
  S.survival = 0; S.shrineTimer = 90;
  keys.up = keys.down = keys.left = keys.right = false;
  S.coinsRun = 0; S.coinsEarned = 0; S.maxCoinsRun = 0; S.gambleCount = 0; S.bossRerollsRun = 0; S.cacheOpens = 0; S.cacheBuys = 0; S.dead = false; S.paused = false; S.dmgBySrc = {};   // fresh damage-meter tally per run
  cam.x = -S.W / 2; cam.y = -S.H / 2; S.shake = 0; S.zoom = 1;
  S.hazards = []; S.hazardTimer = 240; S.mortars = []; S.dmgTexts = []; S.flash = 0;
  S.arena.active = false;
  S.pendingBossAffix = null; S.pendingBossDuo = false; S.bossIntro = null;
  S.pendingUnlocks = [];
  S.fervor = 0; S.fervorTier = 0; S.fvDmg = S.fvFire = S.fvGreed = S.fvSpeed = 1;
  S.goldMult = 1; S.spendMult = 1; S.divBonus = 0; S.adrenalineT = 0;   // coin-powered mults & rush timer don't leak across runs
  S.lastWeapon = -1; S.geminiCd = 0; S.phoenixUsed = false; S.chronoCharge = 0; S.chronoFreezeT = 0; S.mirror = null; S.vaultSpawned = false;   // mythic state
  S.level = 1; S.xp = 0; S.xpNext = xpCost(1); S.pendingLevels = 0; S.levelPerks = {}; S.levelUp = null;   // fresh XP/leveling per run
  S.talentPts = 0; S.talentRanks = {}; S.showTalents = false; S.talentTab = 'fire'; S.talentScroll = 0;   // fresh per-run talent tree (1 pt/level; folds into recomputeStats)
  S.talentFlags = {}; S.orbN = 0; S.precN = 0; S.fanfireUntil = 0;   // fork-mutator flags + proc counters (fanfireUntil: Fan Fire fire-rate burst window)
  S.ascendedRun = {}; S.spinup = 0; S.maulCount = 0; S.paydayCount = 0; S.tempestT = 0; S.stillT = 0; S.chargeT = 0; S.groundFires = [];   // stage-2 (ascension) state
  S.dynamoN = 0; S.bloodscentB = 0; S.avaN = 0; S.mines = [];   // 0.18 item state
  S.spinHeat = 0; S.momentum = 0; S.cramT = 0; S.insightQ = 0; S.ascendQ = 0; S.bomblets = []; S.focusE = null;   // 0.22 new-path transient state
  S.ciMult = 1; S.ggBonus = 0; S.pyroMult = 1;
  generateMap();   // fresh boundary shape + rock formations every run
  S.waveKills = 0; S.waveQuota = 0; S.waveCleared = false; S.waveAge = 0;
  S.biteScale = 1;   // %-max-HP bite depth taper (startWave restamps it per wave) — full strength until deep
  S.graceScale = 1;  // post-hit grace (iFrames) depth taper (startWave restamps it per wave) — full strength until deep, ZERO past BAL.player.graceTaperEnd
  S.voidN = 0;       // Void Rend kill counter (every 8th kill erupts)
  S.panicCd = 0;     // Panic Nova cooldown stamp
  S.lastBreathCd = 0; // Last Breath cooldown stamp
  S.waveEvent = null; S.eventSpeedMul = 1; S.eventCoinMul = 1;
  S.freeRerolls = 0; S.nextShopBoon = false;
  S.threat = 1;   // hpPenalty already zeroed up top, before recomputeStats
  S.bloodConfirm = null; S.pandoraConfirm = null; S.bossReward = null; S.shrineDraft = null;
  S.runTookDamage = false; S.bloodBoughtRun = false; S.lastHitBy = null; S.lastHitDmg = 0; S.lastHitHp = 0; S.evoAnnounced = '';   // fresh per-run achievement flags + death cause + evo fanfare
  S.phoenixUsed = false;   // Phoenix Pact relic: its once-per-run cheat-death is available again
  S.companion = (S.save.companion && S.save.companion !== 'none' && COMPANION_IDX[S.save.companion] != null)
    ? { key: S.save.companion, x: player.x - 36, y: player.y - 36, ph: 0, t: 0 } : null;
}
loadSave();             // boot: hydrate save, derive stats, seed best-display, lint data — before reset()
recomputeStats();
S.best = S.save.best;
validateData();
validateBossKits(BOSS_TYPES, BOSS_NAMES);   // boss kits must cover the roster (rides the DATA LINT gate)
validateRelics(); validateRelicSets();       // relic + set data integrity (rides the DATA LINT gate)
reset();

// map a screen point (mouse) to a world point, for aiming
export function worldFromScreenX(sx) { return player.x + (sx - (player.x - cam.x)) / S.zoom; }
export function worldFromScreenY(sy) { return player.y + (sy - (player.y - cam.y)) / S.zoom; }
// ---- Headless-sim API (sim/) — the live state singletons + the functions the bot drives. NOT used in
// the browser game; sim/sim-entry.js plants this on globalThis so sim/bot.js can play the real bundle. ----
function simEquipRelic(keys, levels) {   // sim A/B helper: equip relic(s) (+ optional {key:star} levels for fusion tests) + recompute
  S.save.relicLoadout = Array.isArray(keys) ? keys.slice() : (keys ? [keys] : []);
  if (levels && typeof levels === 'object') { if (!S.save.relicsOwned) S.save.relicsOwned = {}; for (const k in levels) S.save.relicsOwned[k] = Math.min(3, Math.max(1, levels[k] | 0)); }
  recomputeStats();
}
export const SIM = {
  S, player, cam, aim, keys, WEAPONS, rnd,
  update, die, reset, startRun, recomputeStats, grantItem, startWave,
  takeLevelUp, takeBossReward, buyShrineDraft, closeShrineDraft, confirmBlood, tryInteract, tryDash,
  openShop, closeShop, buyGear,
  GAME_VERSION, ITEMS, LEVEL_STATS, RELICS, equipRelic: simEquipRelic, maybeDropRelic, characterPanel,
};

// ---- Name field for the global leaderboard (shown only on menus) ----
const nameEl = /** @type {HTMLInputElement} */ (document.getElementById('pname'));
if (nameEl && nameEl.addEventListener) {
  nameEl.value = S.save.name;
  nameEl.addEventListener('input', () => { S.save.name = (nameEl.value || '').slice(0, 14) || 'Player'; persist(); });
  nameEl.addEventListener('keydown', e => e.stopPropagation());   // typing must not drive the game (WASD/Space)
  nameEl.addEventListener('mousedown', e => e.stopPropagation());
}
// the controls hint is a fixed glass panel pinned bottom-centre — hide it during active play so it
// stops covering the XP bar (the start/death menu already lists the controls); show it again on menus
export const hintEl = /** @type {any} */ (document.getElementById('hint'));
export function syncNameField() {
  if (nameEl && nameEl.style) nameEl.style.display = ((S.started && !S.dead) || S.showJournal || S.showCharacter) ? 'none' : 'block';
  // desktop keyboard hint only — on touch the menu subtitle carries the controls and this DOM bar just
  // overlaps the menu (inline style here would otherwise beat the CSS media query that hides it)
  if (hintEl && hintEl.style) hintEl.style.display = (S.isTouch || (S.started && !S.dead)) ? 'none' : 'block';
}
fetchGlobal();   // warm the global board so it's ready when the menu opens
// resuming from a live-update swap? jump straight back into a run (the PLAY click happened pre-reload)
try { if (sessionStorage.getItem('vg_autostart')) { sessionStorage.removeItem('vg_autostart'); startRun(); } } catch (e) {}

// Fixed-timestep loop: simulate at a steady 60 updates/sec based on REAL time, so
// the game runs the same speed on any refresh rate, and a big gap (returning from a
// background tab) is clamped to a single step instead of fast-forwarding.
const STEP = 1000 / 60;
S.lastT = performance.now();
S.acc = 0;
function loop(now) {
  const t = now || performance.now();
  let dt = t - S.lastT; S.lastT = t;
  if (dt > 250) dt = STEP;                 // long gap (tab was hidden) → don't catch up
  S.acc += dt;
  let steps = 0;
  while (S.acc >= STEP && steps < 5) {
    update();
    S.acc -= STEP; steps++;
  }
  if (S.acc > STEP * 5) S.acc = 0;             // drop any remaining backlog
  render();
  requestAnimationFrame(loop);
}
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) { S.lastT = performance.now(); S.acc = 0; checkUpdate(); }   // resync clock + catch up on updates on return
});
fetchKnobs();   // live-tuning overrides (knobs.json) — applied on top of the baked-in BAL at boot
setInterval(checkUpdate, 60000); checkUpdate();   // poll the server build stamp (hot-swap between runs)
requestAnimationFrame(loop);
